import { useCallback, useEffect, useRef } from 'react';
import { updateUiPrefsRequest } from '../api/endpoints';
import { useAuth } from '../auth/AuthProvider';
import { setPrefs, usePrefs, type UiPrefs } from '../prefs';
import { reportError } from '../telemetry';
import { useMe } from './useCollaboration';

type Synced = Pick<UiPrefs, 'locale' | 'theme' | 'accent'>;

/**
 * Preferences apply instantly on this device and are saved to the user's profile when signed in,
 * so they follow the person to other devices. On sign-in, the profile's values win.
 */
export function useUiPrefs(): [UiPrefs, (patch: Partial<UiPrefs>) => void] {
  const prefs = usePrefs();
  const { claims } = useAuth();
  const signedIn = claims !== null;
  const save = useCallback(
    (patch: Partial<UiPrefs>) => {
      setPrefs(patch);
      const synced: Partial<Synced> = {};
      if (patch.locale) synced.locale = patch.locale;
      if (patch.theme) synced.theme = patch.theme;
      if (patch.accent) synced.accent = patch.accent;
      if (signedIn && Object.keys(synced).length) updateUiPrefsRequest(synced).catch(reportError); // local change already applied
    },
    [signedIn],
  );
  return [prefs, save];
}

/** Adopts the profile's saved preferences once per sign-in. */
export function useAdoptProfilePrefs(): void {
  const { claims } = useAuth();
  const me = useMe(claims !== null);
  const adoptedFor = useRef<string | null>(null);
  useEffect(() => {
    const ui = me.data?.uiPrefs;
    if (!ui || !claims || adoptedFor.current === claims.userId) return;
    adoptedFor.current = claims.userId;
    setPrefs({ locale: ui.locale, theme: ui.theme, accent: ui.accent });
  }, [me.data, claims]);
}
